import { useEffect, useMemo, useState } from "react";
import "./MarketMonitoring.css";
import { Exchange, Markets } from "./DemoExchangeData";
import Select from "react-select";
import { Filter, Product } from "../../../store/state/MarketMontitoring/types";
import { filters } from "./Filters";
import { COLOR_CODES } from "../../../common";
import {
  ChartExchangeItem,
  ChartMarketProductItem,
  FormatComparativeByMarketProductData,
  FormatMarketMonitoringData,
} from "./FormatData";
import { ChartType, LegendKey } from "../../../models/chart_model";
import { buildFormDataRequest } from "../../../Rest_api/Network_utility";
import { FilterTab } from "../../../components/Filters";
import Dropdown from "../../../components/Dropdown";
import { searchStyle } from "../../../components/Search";
import {
  LegendComponent,
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { DynamicDatePicker } from "../../../components/DatePicker";
import Tooltip from "../../../components/tooltip";

const TABS = ["Comparision"];

type MonthOption = { value: number; label: string };

const EMPTY_MONTH: MonthOption = { value: -1, label: "" };

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const s = saturation / 100;
  const l = lightness / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (hue >= 0 && hue < 60) {
    r = c;
    g = x;
  } else if (hue >= 60 && hue < 120) {
    r = x;
    g = c;
  } else if (hue >= 120 && hue < 180) {
    g = c;
    b = x;
  } else if (hue >= 180 && hue < 240) {
    g = x;
    b = c;
  } else if (hue >= 240 && hue < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  const toHex = (channel: number) =>
    Math.round((channel + m) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function getDistinctLegendColor(legendIndex: number): string {
  const goldenAngle = 137.508;
  const hue = (legendIndex * goldenAngle + 23) % 360;
  const saturationSteps = [78, 72, 66];
  const lightnessSteps = [42, 50, 58];
  const step = legendIndex % saturationSteps.length;

  return hslToHex(hue, saturationSteps[step], lightnessSteps[step]);
}

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const byVolumeKeys: LegendKey[] = [
  {
    dataKey: "dam",
    name: "DAM",
    api_key: "DAM",
    stroke: COLOR_CODES.dam,
  },
  {
    dataKey: "rtm",
    name: "RTM",
    api_key: "RTM",
    stroke: COLOR_CODES.rtm,
  },
  {
    dataKey: "hpdam",
    name: "HPDAM",
    api_key: "HPDAM",
    stroke: COLOR_CODES.hpdAm,
  },
  {
    dataKey: "gdam",
    name: "GDAM",
    api_key: "GDAM",
    stroke: COLOR_CODES.gdam,
  },
  {
    dataKey: "intraDay",
    name: "Intra Day",
    api_key: "Intra-Day Contracts",
    stroke: COLOR_CODES.intraday,
  },
  {
    dataKey: "contingencyContracts",
    name: "DAC",
    api_key: "Day Ahead Contingency Contracts",
    stroke: COLOR_CODES.dac,
  },
  {
    dataKey: "daily",
    name: "Daily",
    api_key: "Daily Contracts",
    stroke: COLOR_CODES.daily,
  },
  {
    dataKey: "weekly",
    name: "Weekly",
    api_key: "Weekly Contracts",
    stroke: COLOR_CODES.weekly,
  },
  {
    dataKey: "monthly",
    name: "Monthly",
    api_key: "Monthly Contracts",
    stroke: COLOR_CODES.monthly,
  },
  {
    dataKey: "singleSided",
    name: "Any Day S.S",
    api_key: "Any Day Single Sided Contracts",
    stroke: COLOR_CODES.anydaySSC,
  },
  {
    dataKey: "bilateral",
    name: "Bilateral",
    api_key: "Bilateral",
    stroke: COLOR_CODES.bilateral,
  },
];

function toggleFilter<T>(selected: T[], all: T[], item: T): T[] {
  if (selected.length === all.length) return [item];
  if (selected.includes(item)) {
    const filtered = selected.filter((i) => i !== item);
    return filtered.length === 0 ? all : filtered;
  }
  return [...selected, item];
}

function toggleMultiFilter<T>(selected: T[], item: T): T[] {
  if (selected.includes(item)) {
    const filtered = selected.filter((i) => i !== item);
    return filtered.length === 0 ? selected : filtered;
  }
  return [...selected, item];
}

function toMonthOrderValue(monthLabel: string | null | undefined): number {
  if (!monthLabel || typeof monthLabel !== "string") {
    return Number.MAX_SAFE_INTEGER;
  }

  const [rawMonth, rawYear] = monthLabel.split("-");
  if (!rawMonth || !rawYear) return Number.MAX_SAFE_INTEGER;

  const month = MONTH_INDEX[rawMonth.slice(0, 3).toLowerCase()];
  const year = Number(rawYear);
  if (month === undefined || Number.isNaN(year)) return Number.MAX_SAFE_INTEGER;

  const fullYear = rawYear.length <= 2 ? 2000 + year : year;
  return fullYear * 12 + month;
}

function sortDataByMonth<T extends { month: string }>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) => toMonthOrderValue(a.month) - toMonthOrderValue(b.month),
  );
}

function getProducts({
  market,
  exchange,
  includeBilateral,
}: {
  market: Filter[];
  exchange: Filter[];
  includeBilateral: boolean;
}): Product[] {
  const PRODUCT_MAPPING: Record<string, string> = {
    DAM: "DAM",
    RTM: "RTM",
    HPDAM: "HPDAM",
    GDAM: "GDAM",
    "Intra-Day Contracts": "Intra Day",
    "Day Ahead Contingency Contracts": "DAC",
    "Daily Contracts": "Daily",
    "Weekly Contracts": "Weekly",
    "Monthly Contracts": "Monthly",
    "Any Day Single Sided Contracts": "Any Day S.S",
    Bilateral: "Bilateral",
  };

  const marketProducts = market.flatMap((m) => m.products);
  const exchangeProducts = exchange.flatMap((e) => e.products);

  const uniqueMarketProducts = Array.from(new Set(marketProducts));
  const uniqueExchangeProducts = Array.from(new Set(exchangeProducts));

  let commonProducts = uniqueMarketProducts.filter((mp) =>
    uniqueExchangeProducts.some((ep) => ep.id === mp.id),
  );

  if (includeBilateral && exchange.some((item) => item.name === "Traders")) {
    commonProducts.push({ id: 13, name: "Bilateral", dname: "Bilateral" });
  }

  return commonProducts.map((item) => ({
    ...item,
    dname: PRODUCT_MAPPING[item.name] ?? item.dname,
  }));
}

function getDefaultByPriceSelection(products: Product[]): number[] {
  if (products.length === 0) {
    return [];
  }

  const damProduct = products.find(
    (item) => item.name === "DAM" || item.dname === "DAM",
  );

  return [damProduct?.id ?? products[0].id];
}

export function MarketMontoring() {
  const [startMonth, setStartMonth] = useState<MonthOption>(EMPTY_MONTH);
  const [endMonth, setEndMonth] = useState<MonthOption>(EMPTY_MONTH);
  const [, setVolumeData] = useState<any[]>([]);
  const [priceData, setPriceData] = useState<any[]>([]);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [selectedMarket, setSelectedMarket] = useState<Filter[]>(Markets);
  const [selectedExchange, setSelectedExchange] = useState<Filter[]>(Exchange);
  const [dates, setDate] = useState(() => {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);
    return [{ startDate: oneMonthAgo, endDate: today, key: "selection" }];
  });
  const [dateRange, setDateRange] = useState(dates[0]);

  const [selectedByPriceProductIds, setSelectedByPriceProductIds] = useState<
    number[]
  >(
    getDefaultByPriceSelection(
      getProducts({
        exchange: Exchange,
        market: Markets,
        includeBilateral: false,
      }),
    ),
  );
  const selectedTab = TABS[0];

  const currentProducts = useMemo(
    () =>
      getProducts({
        exchange: selectedExchange,
        market: selectedMarket,
        includeBilateral: tabIndex === 0,
      }),
    [selectedExchange, selectedMarket, tabIndex],
  );

  const selectedByPriceProducts = useMemo(() => {
    const selectedProducts = currentProducts.filter((item) =>
      selectedByPriceProductIds.includes(item.id),
    );

    if (selectedProducts.length > 0) {
      return selectedProducts;
    }

    const defaultIds = getDefaultByPriceSelection(currentProducts);
    return currentProducts.filter((item) => defaultIds.includes(item.id));
  }, [currentProducts, selectedByPriceProductIds]);

  useEffect(() => {
    if (currentProducts.length === 0) {
      setSelectedByPriceProductIds([]);
      return;
    }

    setSelectedByPriceProductIds((previous) => {
      const availableIds = currentProducts.map((item) => item.id);
      const retainedIds = previous.filter((id) => availableIds.includes(id));
      return retainedIds.length === 0
        ? getDefaultByPriceSelection(currentProducts)
        : retainedIds;
    });
  }, [currentProducts]);

  function fetchAllData(
    overrides: { startMonth?: MonthOption; endMonth?: MonthOption } = {},
  ) {
    const sm = overrides.startMonth ?? startMonth;
    const em = overrides.endMonth ?? endMonth;

    fetchVolumeData({
      selectedMarket,
      selectedProduct: currentProducts,
      startMonth: sm,
      endMonth: em,
    });

    fetchExchangeData({
      selectedMarket,
      startMonth: sm,
      endMonth: em,
    });
  }

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredVolumeKeys = useMemo(
    () =>
      byVolumeKeys.filter((item) =>
        currentProducts.some(
          (product) => product.dname === (item.name ?? item.dataKey),
        ),
      ),
    [currentProducts],
  );

  const monthOptions = useMemo(
    () =>
      [...filters[2].subfilter].reverse().map((item) => ({
        value: Number(item.id),
        label: item.title,
      })),
    [],
  );

  const marketMonitoringChartData = useMemo<ChartExchangeItem[]>(() => {
    const data = FormatMarketMonitoringData(
      priceData,
      selectedExchange.map((e) => e.name),
      [...selectedMarket.map((e) => e.name), "Traders", "Bilateral"],
      true,
    );

    return sortDataByMonth(data);
  }, [priceData, selectedExchange, selectedMarket]);

  const productDisplayNameMap = useMemo(() => {
    const map: Record<string, string> = {
      "Trading Licensees": "Bilateral",
    };

    currentProducts.forEach((item) => {
      map[item.name] = item.dname ?? item.name;
    });

    return map;
  }, [currentProducts]);

  const comparativeChartData = useMemo<{
    rows: ChartMarketProductItem[];
    series: {
      name: string;
      priceKey: string;
      volumeKey: string;
      exchange: string;
      market: string;
      product: string;
    }[];
  }>(() => {
    return FormatComparativeByMarketProductData(
      priceData,
      [
        ...selectedByPriceProducts.map((item) => item.name),
        "Trading Licensees",
      ],
      [...selectedMarket.map((item) => item.name), "Traders"],
      productDisplayNameMap,
      [...new Set([...selectedExchange.map((item) => item.name), "Traders"])],
    );
  }, [
    priceData,
    selectedByPriceProducts,
    selectedMarket,
    productDisplayNameMap,
    selectedExchange,
  ]);

  const comparativeLegends = useMemo<LegendKey[]>(() => {
    return comparativeChartData.series.flatMap((series, index) => {
      const volumeColor = getDistinctLegendColor(index * 2);
      const priceColor = getDistinctLegendColor(index * 2 + 1);

      return [
        {
          dataKey: series.volumeKey,
          name: `${series.name} Volume`,
          type: ChartType.Bar,
          yAxisId: "right",
          stroke: volumeColor,
        },
        {
          dataKey: series.priceKey,
          name: `${series.name} Price`,
          stroke: priceColor,
        },
      ];
    });
  }, [comparativeChartData.series]);

  function handleMonthChange(
    type: "start" | "end",
    selectedOption: { value: number; label: string } | null,
  ) {
    const newMonth = selectedOption
      ? { value: selectedOption.value as number, label: selectedOption.label }
      : EMPTY_MONTH;

    if (type === "start") {
      setStartMonth(newMonth);
      fetchAllData({ startMonth: newMonth });
      return;
    }

    setEndMonth(newMonth);
    fetchAllData({ endMonth: newMonth });
  }

  const footer = (
    <div className="gna-footer-container">
      <div className="grid grid-cols-3 justify-between gap-5 ">
        <Tooltip note={<p>Market Monitoring</p>} text=" ">
          <p className="gna-footer-text">Source</p>
        </Tooltip>
        <p className="gna-footer-text col-span-1 text-center">
          Powered by <strong>GNA Energy</strong>
        </p>
        <strong className="gna-footer-text col-span-1 text-end">
          Beta Version
        </strong>
      </div>
    </div>
  );

  return (
    <div>
      <div className="header">
        <h1>Market Monitoring</h1>

        {/* <FilterTab
          options={TABS}
          selected={[selectedTab]}
          onClick={(option) => setSelectedTab(option)}
        /> */}
        <div className="header-side-area">
          {selectedTab === "Comparision" && (
            <div className="flex space-x-2">
              <Select
                placeholder="Start Month"
                isClearable={true}
                styles={searchStyle as any}
                className="dateSelect"
                value={startMonth.value !== -1 ? startMonth : undefined}
                options={monthOptions.map((item) => ({
                  ...item,
                  isDisabled:
                    endMonth.value !== -1 && item.value >= endMonth.value,
                }))}
                onChange={(opt) => handleMonthChange("start", opt)}
              />
              <Select
                placeholder="End Month"
                className="dateSelect"
                isClearable={true}
                styles={searchStyle as any}
                value={endMonth.value !== -1 ? endMonth : undefined}
                options={monthOptions.map((item) => ({
                  ...item,
                  isDisabled: item.value <= startMonth.value,
                }))}
                onChange={(opt) => handleMonthChange("end", opt)}
              />
            </div>
          )}
          {selectedTab === "Analysis" && (
            <DynamicDatePicker
              defaultDates={dateRange}
              setDate={(date) => {
                setDateRange({
                  startDate: date[0].startDate,
                  endDate: date[0].endDate,
                  key: "selection",
                });
                setDate(date);
              }}
            />
          )}
        </div>
      </div>
      {selectedTab === "Comparision" && (
        <div className="c">
          <div className="body-column-container">
            <div className="filter-horizontal mt-2">
              <div className="flex items-center space-x-8">
                <Dropdown
                  options={["Cumulative", "Comparative"]}
                  selected={["Cumulative", "Comparative"][tabIndex]}
                  onSelect={(_option, index) => setTabIndex(index)}
                />

                {tabIndex === 0 && (
                  <FilterTab
                    options={Exchange.map((item) => item.name)}
                    selected={selectedExchange.map((item) => item.name)}
                    allowMultiple={true}
                    isSecondary={true}
                    isRounded={true}
                    onClick={(_option, index) =>
                      setSelectedExchange(
                        toggleFilter(
                          selectedExchange,
                          Exchange,
                          Exchange[index],
                        ),
                      )
                    }
                  />
                )}

                {tabIndex === 1 && (
                  <FilterTab
                    options={currentProducts.map(
                      (item) => item.dname ?? item.name,
                    )}
                    selected={selectedByPriceProducts.map(
                      (item) => item.dname ?? item.name,
                    )}
                    allowMultiple={true}
                    onClick={(_option, index) => {
                      const selectedItem = currentProducts[index];
                      if (!selectedItem) return;

                      setSelectedByPriceProductIds((previous) =>
                        toggleMultiFilter(previous, selectedItem.id),
                      );
                    }}
                  />
                )}
              </div>
              <FilterTab
                options={Markets.map((item) => item.name)}
                selected={selectedMarket.map((item) => item.name)}
                allowMultiple={true}
                onClick={(_option, index) =>
                  setSelectedMarket(
                    toggleFilter(selectedMarket, Markets, Markets[index]),
                  )
                }
              />
            </div>

            <div className="markeMonitoring-chart-container">
              <div className="markeMonitoring-chart">
                {tabIndex === 0 && (
                  <>
                    <div className="flex gap-x-8 justify-center">
                      {LegendComponent(
                        {
                          dataKey: "total_mu",
                          stroke: COLOR_CODES.secondary,
                          name: "Volume(MUs)",
                          type: 1,
                          legendColor: COLOR_CODES.secondary,
                        },
                        0,
                        [],
                        () => {},
                      )}
                      {LegendComponent(
                        {
                          dataKey: "weighted_avg_rs_per_kwh",
                          stroke: COLOR_CODES.secondary,
                          name: "Wt. Avg Price(Rs/KWh)",
                          yAxisId: "right",
                        },
                        1,
                        [],
                        () => {},
                      )}
                    </div>
                    <ReMixChart
                      yAxisLabel={"Weighted Average Price (Rs/KWh)"}
                      secondYAxisWidth={80}
                      showBrush={true}
                      xLabel=""
                      yAxisWidth={50}
                      unit={"MUs"}
                      data={marketMonitoringChartData as any}
                      brushIndex={{
                        startIndex: Math.max(
                          0,
                          marketMonitoringChartData.length - 12,
                        ),
                        endIndex: Math.max(
                          0,
                          marketMonitoringChartData.length - 1,
                        ),
                      }}
                      legendBreakIndex={filteredVolumeKeys.length - 1}
                      secondYAxisLabel="Volume (MU)"
                      legends={[
                        ...filteredVolumeKeys.map((item) => ({
                          dataKey: item.dataKey + "_volume",
                          name: item.name,
                          stroke: lightenColor(item.stroke as any, 20),
                          yAxisId: "right",
                          type: ChartType.Bar,
                        })),
                        ...filteredVolumeKeys.map((item) => ({
                          dataKey: item.dataKey,
                          name: item.name + " Price",
                          stroke: item.stroke,
                        })),
                      ]}
                      xDataKey="month"
                    />
                    {footer}
                  </>
                )}
                {tabIndex === 1 && (
                  <>
                    {/* <div className="flex justify-center">
                      <h2 className="chartHeading">
                        Combination Comparison (Market - Product)
                      </h2>
                    </div> */}
                    <ReMixChart
                      showBrush={true}
                      xLabel=""
                      yAxisLabel={"Weighted Average Price (Rs/KWh)"}
                      secondYAxisLabel="Volume (MU)"
                      yAxisWidth={80}
                      data={comparativeChartData.rows as any}
                      brushIndex={{
                        startIndex: Math.max(
                          0,
                          comparativeChartData.rows.length - 12,
                        ),
                        endIndex: Math.max(
                          0,
                          comparativeChartData.rows.length - 1,
                        ),
                      }}
                      legends={comparativeLegends}
                      unit={"MU"}
                      xDataKey="month"
                    />
                    {footer}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* {selectedTab === "Analysis" && <MarketAnalysis dates={dates} />} */}
    </div>
  );

  async function fetchVolumeData({
    selectedMarket,
    selectedProduct,
    startMonth,
    endMonth,
  }: {
    selectedMarket: Filter[];
    selectedProduct: Product[];
    startMonth: MonthOption;
    endMonth: MonthOption;
  }) {
    const res = await buildFormDataRequest({
      endpoint: "data/market_monitoring_volume_api",
      method: "POST",
      body: {
        exchange: "HPX,IEX,PXIL,Traders",
        market: selectedMarket.map((item) => item.name).toString(),
        product: selectedProduct.map((item) => item.name).toString(),
        start_month: startMonth.label,
        end_month: endMonth.label,
      },
    });

    setVolumeData(res);
  }

  async function fetchExchangeData({
    selectedMarket,
    startMonth,
    endMonth,
  }: {
    selectedMarket: Filter[];
    startMonth: MonthOption;
    endMonth: MonthOption;
  }) {
    const byPrice = await buildFormDataRequest({
      endpoint: "data/market_monitoring_price_api",
      method: "POST",
      body: {
        exchange: "HPX,IEX,PXIL,Traders",
        market: selectedMarket.map((item) => item.name).toString(),
        product:
          byVolumeKeys.map((item) => item.api_key).toString() +
          ",Trading Licensees",
        start_month: startMonth.label,
        end_month: endMonth.label,
      },
    });

    setPriceData(byPrice);
  }
}
